import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
/* ================= INITIAL STATE ================= */
const initialState = {
    clientListWithIcp: [],
    clientListWithIcpLoading: false,
    clientListWithIcpError: null,
    clientListWithIcpRequestId: null,
    clientListWithIcpPagination: { page: 1, totalPages: 1, totalEntries: 0 },
    clientListWithIcpCounts: { active: 0, inactive: 0, total: 0 },
    categoryOptions: [],
    affiliateNetworkOptions: [],
    sampleStatusOptions: [],
    dropdownOptionsLoading: false,
};
/* ================= ASYNC THUNKS ================= */
export const fetchClientListWithIcp = createAsyncThunk("activeClientList/fetchClientListWithIcp", async ({ params }, { getState, rejectWithValue, signal }) => {
    try {
        const token = getState().auth?.token;
        const entityType = getState().auth?.entity_type;
        if (entityType)
            params.append('entity_type', entityType);
        const res = await ApiCall("GET", `/api/icp/list-with-icp?${params.toString()}`, "", { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' }, undefined, "json", signal);
        if (signal.aborted) {
            return rejectWithValue("Request aborted");
        }
        if (res && "data" in res && res.status === 200 && res.data?.success) {
            return res.data.data;
        }
        return rejectWithValue(res?.data?.message || "Failed to fetch client list with ICP");
    }
    catch (err) {
        if (signal.aborted) {
            return rejectWithValue("Request aborted");
        }
        return rejectWithValue(err?.message || "Fetch client list with ICP error");
    }
});
export const bulkUpdateClientIcpFields = createAsyncThunk("activeClientList/bulkUpdateClientIcpFields", async ({ updates }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("PATCH", `/api/icp/icp/bulk-update`, { updates }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            const { results, summary } = res.data.data;
            toast.success(res.data?.message || `Successfully updated ${summary.succeeded} client(s)`);
            return { results, summary };
        }
        const msg = res?.data?.message || "Failed to bulk update.";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Bulk update failed.";
        toast.warning(msg);
        return rejectWithValue(msg);
    }
});
export const fetchDropdownOptions = createAsyncThunk("activeClientList/fetchDropdownOptions", async (type, { rejectWithValue }) => {
    try {
        const res = await ApiCall("GET", `/api/utility/options/${type}`, null, {});
        if (res && "data" in res && res.data?.success) {
            return { type, options: res.data.data };
        }
        return rejectWithValue(res?.data?.message || "Failed to fetch options");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Fetch options error");
    }
});
/* ================= SLICE ================= */
const activeClientListSlice = createSlice({
    name: "activeClientList",
    initialState,
    reducers: {},
    extraReducers: (builder) => builder
        .addCase(fetchClientListWithIcp.pending, (state, action) => {
        state.clientListWithIcpLoading = true;
        state.clientListWithIcpError = null;
        state.clientListWithIcpRequestId = action.meta.requestId;
    })
        .addCase(fetchClientListWithIcp.fulfilled, (state, action) => {
        if (state.clientListWithIcpRequestId !== action.meta.requestId) {
            return;
        }
        state.clientListWithIcpLoading = false;
        state.clientListWithIcp = action.payload.clients || [];
        state.categoryOptions = Array.isArray(action.payload.categoryOptions)
            ? action.payload.categoryOptions
            : [];
        state.clientListWithIcpCounts = {
            active: action.payload.active || 0,
            inactive: action.payload.inactive || 0,
            total: action.payload.total || 0,
        };
        const params = action.meta.arg.params;
        const status = params.get("status");
        const searchText = params.get("searchText");
        let actualTotal = action.payload.total || 0;
        if (!searchText) {
            if (status === "active")
                actualTotal = action.payload.active || 0;
            else if (status === "inactive")
                actualTotal = action.payload.inactive || 0;
        }
        const limit = Number(params.get("limit") || 10);
        state.clientListWithIcpPagination = {
            page: action.payload.page || 1,
            totalPages: Math.ceil(actualTotal / limit) || 1,
            totalEntries: actualTotal,
        };
    })
        .addCase(fetchClientListWithIcp.rejected, (state, action) => {
        if (action.meta.aborted) {
            return;
        }
        if (state.clientListWithIcpRequestId === action.meta.requestId) {
            state.clientListWithIcpLoading = false;
            state.clientListWithIcpError = action.payload || null;
        }
    })
        .addCase(bulkUpdateClientIcpFields.fulfilled, (state, action) => {
        const { results } = action.payload;
        results.forEach(({ clientId, icp }) => {
            const idx = state.clientListWithIcp.findIndex((c) => c._id === clientId);
            if (idx !== -1) {
                state.clientListWithIcp[idx] = {
                    ...state.clientListWithIcp[idx],
                    icp: { ...state.clientListWithIcp[idx].icp, ...icp },
                };
            }
        });
    })
        .addCase(fetchDropdownOptions.pending, (state) => {
        state.dropdownOptionsLoading = true;
    })
        .addCase(fetchDropdownOptions.fulfilled, (state, action) => {
        state.dropdownOptionsLoading = false;
        if (action.payload.type === 'affiliate-networks') {
            state.affiliateNetworkOptions = action.payload.options;
        }
        else if (action.payload.type === 'sample-status') {
            state.sampleStatusOptions = action.payload.options;
        }
    })
        .addCase(fetchDropdownOptions.rejected, (state) => {
        state.dropdownOptionsLoading = false;
    }),
});
/* ================= SELECTORS ================= */
export const selectClientListWithIcp = (state) => state.activeClientList.clientListWithIcp;
export const selectClientListWithIcpLoading = (state) => state.activeClientList.clientListWithIcpLoading;
export const selectClientListWithIcpError = (state) => state.activeClientList.clientListWithIcpError;
export const selectClientListWithIcpPagination = (state) => state.activeClientList.clientListWithIcpPagination;
export const selectClientListWithIcpCounts = (state) => state.activeClientList.clientListWithIcpCounts;
export const selectCategoryOptions = (state) => state.activeClientList.categoryOptions;
export const selectAffiliateNetworkOptions = (state) => state.activeClientList.affiliateNetworkOptions;
export const selectSampleStatusOptions = (state) => state.activeClientList.sampleStatusOptions;
export const selectDropdownOptionsLoading = (state) => state.activeClientList.dropdownOptionsLoading;
export default activeClientListSlice.reducer;
